import { useAuthenticatedAxiosInstance } from './useAuthenticatedAxiosInstance';
import { useCallback } from 'react';
import { GraphPackage, GraphDescription } from '../GraphList/types';
import { AxiosResponse } from 'axios';
import { getEnvironmentConfig } from '../environmentConfig';

export const useGraphStorageApi = () => {
    const api = useAuthenticatedAxiosInstance(`${getEnvironmentConfig().graphServiceUrl}`);

    const getAllGraphDescriptions = useCallback(async (): Promise<GraphDescription[]> => {
        const result: AxiosResponse = await api.get(`/graphs`);
        return result.data.graphDescriptions;
    }, [api]);

    const getGraph = useCallback(
        async (graphId: string): Promise<GraphPackage & { orgId: string | null }> => {
            const result: AxiosResponse = await api.get(`/graphs/${graphId}`);
            const data = result.data;
            const graphPackage: GraphPackage = data.graph.variables
                ? { graph: data.graph, metadata: data.metadata }
                : { metadata: data.metadata, graph: { ...data.graph, variables: [] } };
            return { ...graphPackage, orgId: data.orgId ?? null };
        },
        [api],
    );

    const deleteGraph = useCallback(
        async (graphId: string): Promise<void> => {
            await api.delete(`/graphs/${graphId}`);
            return;
        },
        [api],
    );

    const renameGraph = useCallback(
        async (graphId: string, name: string): Promise<void> => {
            await api.patch(`/graphs/${graphId}`, { name });
            return;
        },
        [api],
    );

    const updateGraph = useCallback(
        async (graphId: string, graphPackage: GraphPackage): Promise<void> => {
            const result: AxiosResponse = await api.put(`/graphs/${graphId}`, {
                graphPackage: graphPackage,
            });
            return result.data;
        },
        [api],
    );

    const createGraph = useCallback(
        async (graphPackage: GraphPackage): Promise<string> => {
            const result: AxiosResponse = await api.post(`/graphs`, {
                graphPackage: graphPackage,
            });
            return result.data.graphId;
        },
        [api],
    );

    return {
        getAllGraphDescriptions,
        getGraph,
        updateGraph,
        createGraph,
        deleteGraph,
        renameGraph,
    };
};
